import React, { FC, MouseEvent, ReactNode, useRef, useState } from 'react';
import {
  Button,
  ButtonProps,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  PopoverProps,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import Container from './Container';

export interface AcceptButtonProps
  extends Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin'>,
    Pick<ButtonProps, 'color'> {
  acceptIcon?: ReactNode;
  iconColor?: 'action' | 'disabled' | 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  acceptLabel?: string | 'accept' | 'save';
  onConfirm: () => void;
  onCancel?: () => void;
  component?: 'Button' | 'MenuItem';
  disabled?: boolean;
}

export const AcceptButton: FC<AcceptButtonProps> = ({
  acceptIcon,
  acceptLabel = 'accept',
  onConfirm,
  onCancel,
  anchorOrigin,
  transformOrigin,
  component,
  disabled,
  color = 'inherit',
  iconColor = 'success',
}) => {
  const button = useRef<HTMLElement | null>(null);

  const { t } = useTranslation('components');

  const [open, setOpen] = useState<boolean>(false);

  const openMenu = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
  };

  const closeMenu = () => setOpen(false);

  const handleConfirm = () => {
    closeMenu();
    onConfirm?.();
  };
  const handleCancel = () => {
    closeMenu();
    onCancel?.();
  };
  const handleClose = (e: MouseEvent) => {
    e.stopPropagation();
    closeMenu();
    onCancel?.();
  };

  const getAcceptLabel = (label?: string) => {
    if (!label || label === 'accept') return t('AcceptButton.DefaultButtonLabel');
    if (label === 'save') return t('AcceptButton.AlternativeButtonLabel');
    return label;
  };

  const renderButton = () => {
    const icon = acceptIcon || <MaterialSymbol name='done' color={iconColor} />;
    const label = getAcceptLabel(acceptLabel);
    if (component === 'MenuItem') {
      return (
        <MenuItem
          ref={(el) => {
            button.current = el;
          }}
          aria-label={label}
          aria-haspopup='true'
          aria-controls='confirm-accept-dialog'
          onClick={openMenu}>
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText>{label}</ListItemText>
        </MenuItem>
      );
    }
    return (
      <Button
        ref={(el) => {
          button.current = el;
        }}
        color={color}
        aria-haspopup='true'
        aria-controls='confirm-accept-dialog'
        onClick={openMenu}
        startIcon={icon}
        disabled={disabled}>
        {label}
      </Button>
    );
  };

  return (
    <>
      {renderButton()}
      <Popover
        id='confirm-accept-dialog'
        anchorEl={button.current}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}>
        <Paper>
          <Container column p={1}>
            <Typography>{t('AcceptButton.ConfirmDeleteQuestion')}</Typography>
            <Container right>
              <Button variant='outlined' color='secondary' onClick={handleCancel}>
                {t('AcceptButton.AbortOption')}
              </Button>
              <Button variant='contained' color='success' onClick={handleConfirm}>
                {t('AcceptButton.ConfirmOption')}
              </Button>
            </Container>
          </Container>
        </Paper>
      </Popover>
    </>
  );
};

export default AcceptButton;
